/* eslint-disable max-len */
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, shallowEqual } from 'react-redux';
import { useFela } from 'react-fela';
import { Text } from '../../common';
import { selectNormalizedCurrentTime, selectNormalizedInterval } from '../../../selectors';
import { largeScreen, smallScreen } from '../../../../common/mixins';
import { usePlayer } from '../../../PlayerContext';

const container = ({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  position: 'absolute',
  right: theme.margin.small,
  bottom: '8rem',
  marginRight: theme.margin.xxlarge,
  extend: [
    largeScreen({
      marginRight: theme.margin.large,
    }),
    smallScreen({
      marginRight: theme.margin.medium,
    }),
  ],
});

const closeButton = ({ theme }) => ({
  color: 'white',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: theme.fontSize.normal,
  paddingLeft: theme.margin.medium,
  paddingRight: theme.margin.medium,
  marginRight: theme.margin.medium,
  height: '3.5rem',
  borderRadius: '5px',
  backgroundColor: theme.color.secondaryButtonBackground,
  transition: `background-color ${theme.transition.default}`,
  ':hover': {
    backgroundColor: theme.color.secondaryButtonBackgroundHover,
  },
});

const goNextButton = ({ theme }) => ({
  color: 'black',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: theme.fontSize.normal,
  paddingLeft: theme.margin.medium,
  paddingRight: theme.margin.medium,
  gap: theme.margin.fine,
  height: '3.5rem',
  borderRadius: '5px',
  backgroundColor: theme.color.buttonColor,
  opacity: theme.opacity.hovered,
  transition: `background-color ${theme.transition.default}`,
  ':hover': {
    backgroundColor: theme.color.buttonColorHover,
  },
});

const NextVideo = ({ hide, settings }) => {
  const player = usePlayer();
  const closingCreditsStart = player.model.viewable?.defaultPlayable?.closingCreditsStart;
  const [,, duration] = useSelector(selectNormalizedInterval, shallowEqual);
  const currentTime = useSelector(selectNormalizedCurrentTime);
  const {
    playing,
    isBuffering,
  } = useSelector(state => ({
    playing: state.playback.playing,
    isBuffering: state.playback.isBuffering,
  }));
  const [close, setClose] = useState(!settings.countdown);
  const timer = useRef(null);
  const [countdown, setCountdown] = useState(null);
  const countdownRef = useRef(null);
  const timeLeft = useRef(0);

  if (duration && currentTime) {
    timeLeft.current = Math.max(0, Math.floor(duration - currentTime));
  } else {
    timeLeft.current = 0;
  }

  const closingCredits = useMemo(() => {
    if (closingCreditsStart) return closingCreditsStart;
    if (!duration) return 0;
    // Min value is 50% of the duration
    return Math.max(duration - settings.endTimeOffset, duration / 2);
  }, [duration, closingCreditsStart]);
  const isVisible = !!duration && closingCredits <= currentTime;

  const stopTimer = () => {
    clearInterval(timer.current);
    timer.current = null;
  };

  // manage the countdown timer
  useEffect(() => {
    if (!settings.countdown) return;

    const changeCountdown = (value) => {
      countdownRef.current = value;
      setCountdown(value);
    };
    // scroll back or X click - clean up
    if ((!isVisible || close) && (timer.current || countdownRef.current)) {
      stopTimer();
      changeCountdown(null);
    }
    // pause or buffering - stop timer but save countdown
    if ((!playing || isBuffering) && timer.current) {
      stopTimer();
    }
    // update countdown if no enough time left
    if (timeLeft.current < countdown) {
      changeCountdown(timeLeft.current);
    }
    // init/update countdown and start or resume timer if possible
    if (isVisible && !timer.current && !close) {
      // start new countdown
      if (!countdownRef.current) {
        const time = Math.min(timeLeft.current, settings.countdown);
        changeCountdown(time);
      }
      // start countdown interval
      if (!isBuffering && playing && countdownRef.current) {
        timer.current = setInterval(() => {
          if (!countdownRef.current) return;
          // decrement countdown. in case of scroll - find smaller
          const time = Math.min(countdownRef.current - 1, timeLeft.current);
          changeCountdown(time);

          // trigger nextVideo event if countdown ends
          if (!countdownRef.current) {
            player.dispatchEvent('nextVideo');
            stopTimer();
          }
        }, 1000);
      }
    }
  }, [isVisible, close, playing, isBuffering, timeLeft.current < countdown]);

  useEffect(() => () => clearInterval(timer.current), []);

  const clickHandler = useCallback(() => {
    stopTimer();

    player.dispatchEvent('nextVideo');
  }, []);

  const { css } = useFela({ visible: false });

  if (hide || !isVisible) return null;

  return (
    <div className={css(container)}>
      {!close && (
        <div className={css(closeButton)} onClick={() => setClose(true)}>
          <Text bold id="nextVideo.watchCredits" />
        </div>
      )}
      <div className={css(goNextButton)} onClick={clickHandler}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20.25 11.567C20.5833 11.7594 20.5833 12.2406 20.25 12.433L8.25 19.3612C7.91667 19.5537 7.5 19.3131 7.5 18.9282L7.5 5.0718C7.5 4.6869 7.91667 4.44634 8.25 4.63879L20.25 11.567Z" fill="currentColor" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span>
          {settings.title || (<Text bold id="nextVideo.label" />)} {countdown !== null && (
            <Text bold id="nextVideo.timer" values={{ time: countdown }} />
          )}
        </span>
      </div>
    </div>
  );
};

NextVideo.propTypes = {
  hide: PropTypes.bool,
  settings: PropTypes.object,
};

export default NextVideo;
